import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * @typedef {Object} SfHeaderRefs
 * @property {HTMLElement[]} menuTrigger - Navigation menu trigger links
 * @property {HTMLElement} [cartCount] - Cart count badge
 * @property {HTMLElement} [cartCountMobile] - Mobile cart count badge
 */

/**
 * Custom header component for Sterling Forever.
 *
 * @extends {Component<SfHeaderRefs>}
 */
class SfHeader extends Component {
  /** @type {number} */
  #lastScrollY = 0;

  /** @type {number | null} */
  #scrollRafId = null;

  /** @type {HTMLElement | null} */
  #announcementBar = null;

  /** @type {ResizeObserver} */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry || !entry.borderBoxSize[0]) return;
    const height = Math.round(entry.borderBoxSize[0].blockSize);
    document.documentElement.style.setProperty('--sf-header-height', `${height}px`);
  });

  /** @type {ResizeObserver | null} */
  #announcementResizeObserver = null;

  /** @returns {number} */
  get #threshold() {
    const value = parseInt(this.dataset.threshold ?? '', 10);
    return Number.isNaN(value) ? 50 : value;
  }

  /** @returns {'none' | 'on-scroll-up' | 'always'} */
  get #stickyMode() {
    const mode = this.dataset.sticky;
    if (mode === 'on-scroll-up' || mode === 'always') return mode;
    return 'none';
  }

  connectedCallback() {
    super.connectedCallback();

    this.#resizeObserver.observe(this);
    this.#setupSticky();

    document.addEventListener('scroll', this.#handleScroll, { passive: true });
    document.addEventListener('sf-mega-menu:open', this.#handleMegaMenuOpen);
    document.addEventListener('sf-mega-menu:close', this.#handleMegaMenuClose);
    document.addEventListener('sf-search:open', this.#handleSearchOpen);
    document.addEventListener('sf-search:close', this.#handleSearchClose);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);

    this.#lastScrollY = window.scrollY;
    this.#applyScrollState();

    window.addEventListener('resize', this.#updateDesktopTopBound, { passive: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#resizeObserver.disconnect();
    this.#announcementResizeObserver?.disconnect();
    document.documentElement.style.removeProperty('--sf-announcement-height');
    document.removeEventListener('scroll', this.#handleScroll);
    document.removeEventListener('sf-mega-menu:open', this.#handleMegaMenuOpen);
    document.removeEventListener('sf-mega-menu:close', this.#handleMegaMenuClose);
    document.removeEventListener('sf-search:open', this.#handleSearchOpen);
    document.removeEventListener('sf-search:close', this.#handleSearchClose);
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);

    if (this.#scrollRafId !== null) {
      cancelAnimationFrame(this.#scrollRafId);
    }

    document.documentElement.style.removeProperty('--sf-header-height');

    window.removeEventListener('resize', this.#updateDesktopTopBound);
  }

  /**
   * Makes parent wrappers pass-through so sticky works.
   *
   * Shopify wraps sections in:
   *   <div id="header-group">
   *     <section class="shopify-section">
   *       <sf-header>
   *
   * Both need display:contents so sf-header becomes a direct
   * layout child of <body>, enabling position:sticky.
   */
  #setupSticky() {
    if (this.#stickyMode === 'none') return;

    const shopifySection = this.closest('.shopify-section');
    if (shopifySection) {
      shopifySection.style.display = 'contents';

      const headerGroup = shopifySection.parentElement;
      if (headerGroup?.id === 'header-group') {
        headerGroup.style.display = 'contents';
      }
    }

    this.#setupAnnouncementBar();

    if (this.#stickyMode === 'always') {
      this.style.position = 'sticky';
      this.style.top = 'var(--sf-announcement-height, 0px)';
      this.style.zIndex = '10';

      if (this.#announcementBar) {
        this.#announcementBar.style.position = 'sticky';
        this.#announcementBar.style.top = '0';
        this.#announcementBar.style.zIndex = '11';
      }
    }

    this.#setupTransparent();
  }

  /**
   * Locate the announcement bar inside the header-group, flatten its wrapper
   * sections via display:contents so position:sticky works, and track its
   * height into --sf-announcement-height. Coordinated with header sticky.
   */
  #setupAnnouncementBar() {
    const bar = document.querySelector('.announcement-bar');
    if (!bar) return;

    this.#announcementBar = bar;

    let node = bar.parentElement;
    while (node && node !== document.body) {
      if (node.classList?.contains('shopify-section') || node.id === 'header-group') {
        node.style.display = 'contents';
      }
      node = node.parentElement;
    }

    const setVar = (h) => {
      document.documentElement.style.setProperty('--sf-announcement-height', `${h}px`);
    };
    setVar(Math.round(bar.getBoundingClientRect().height));

    this.#announcementResizeObserver = new ResizeObserver(([entry]) => {
      if (!entry || !entry.borderBoxSize[0]) return;
      setVar(Math.round(entry.borderBoxSize[0].blockSize));
    });
    this.#announcementResizeObserver.observe(bar);
  }

  #updateDesktopTop() {
  if (window.innerWidth < 990) return;

    const header = document.querySelector('sf-header');
    if (!header) return;

    const rect = header.getBoundingClientRect();
    const top = Math.max(rect.bottom, 0);

    document.documentElement.style.setProperty('--sf-search-top', `${Math.round(top)}px`);
  }

  #updateDesktopTopBound = () => {
    this.#updateDesktopTop();
  };

  /**
   * For transparent headers, the header stays in flow but uses
   * negative margin-bottom to collapse its space. Content after
   * it naturally flows up behind the transparent header.
   */
  #setupTransparent() {
    if (this.dataset.transparent !== 'true') return;

    this.style.marginBottom = `calc(var(--sf-header-height, 77px) * -1)`;
    this.style.position = 'relative';
    this.style.zIndex = '10';
  }

  /** @type {() => void} */
  #handleScroll = () => {
    if (this.#scrollRafId !== null) return;

    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      this.#applyScrollState();
    });
  };

  #applyScrollState() {
    const scrollY = window.scrollY;
    const isScrolled = scrollY > this.#threshold;
    const direction = scrollY > this.#lastScrollY ? 'down' : 'up';
    const headerHeight = this.offsetHeight || 80;
    const wasScrolled = this.dataset.scrolled === 'true';
    const headerInner = this.querySelector('.sf-header');

    if (this.dataset.transparent === 'true' && headerInner && wasScrolled !== isScrolled) {
      headerInner.style.transition = 'background-color 0.4s ease, color 0.4s ease';
    }

    this.dataset.scrolled = String(isScrolled);

    if (this.#stickyMode === 'on-scroll-up') {
      const scrolledPast = scrollY > headerHeight;

      if (scrolledPast && direction === 'up') {
        this.dataset.stickyState = 'active';
        this.style.position = 'sticky';
        this.style.top = 'var(--sf-announcement-height, 0px)';
        this.style.zIndex = '10';

        if (this.#announcementBar) {
          this.#announcementBar.style.position = 'sticky';
          this.#announcementBar.style.top = '0';
          this.#announcementBar.style.zIndex = '11';
        }
      } else if (direction === 'down' && this.dataset.stickyState === 'active') {
        this.dataset.stickyState = 'idle';

        if (this.#announcementBar) {
          this.#announcementBar.style.position = '';
          this.#announcementBar.style.top = '';
          this.#announcementBar.style.zIndex = '';
        }
      }

      if (scrollY <= 0) {
        this.dataset.stickyState = 'inactive';

        if (this.dataset.transparent === 'true') {
          this.style.position = 'relative';
          this.style.zIndex = '10';
        } else {
          this.style.position = '';
          this.style.zIndex = '';
        }

        this.style.top = '';

        if (this.#announcementBar) {
          this.#announcementBar.style.position = '';
          this.#announcementBar.style.top = '';
          this.#announcementBar.style.zIndex = '';
        }
      }
    }

    this.#lastScrollY = scrollY;
  }

  /** @type {() => void} */
  #handleMegaMenuOpen = () => {
    this.dataset.megaOpen = 'true';
  };

  /** @type {() => void} */
  #handleMegaMenuClose = () => {
    this.dataset.megaOpen = 'false';
  };

  /** @type {() => void} */
  #handleSearchOpen = () => {
    this.#updateDesktopTop();

    const megaMenu = this.querySelector('sf-mega-menu');
    if (megaMenu && typeof megaMenu.closeAll === 'function') {
      megaMenu.closeAll();
    }
    this.dataset.megaOpen = 'true';
  };

  /** @type {() => void} */
  #handleSearchClose = () => {
    this.dataset.megaOpen = 'false';
  };

  /**
   * @param {CustomEvent} event
   */
  #handleCartUpdate = (event) => {
    const itemCount = event.detail?.data?.itemCount ?? event.detail?.resource?.item_count;
    if (itemCount === undefined) return;

    const countText = String(itemCount);

    if (this.refs.cartCount) {
      this.refs.cartCount.textContent = countText;
    }

    if (this.refs.cartCountMobile) {
      this.refs.cartCountMobile.textContent = countText;
    }
  };
}

if (!customElements.get('sf-header')) {
  customElements.define('sf-header', SfHeader);
}

let swymProductsPromise = null;

class WishlistButton extends HTMLElement {
  constructor() {
    super();
    this.button = this.querySelector('button');
    this.handleUpdate = this.checkIfSaved.bind(this);
  }

  connectedCallback() {
    if (window._swat) {
      this.init();
    } else {
      if (!window.SwymCallbacks) window.SwymCallbacks = [];
      window.SwymCallbacks.push(this.init.bind(this));
    }

    window.addEventListener('wishlist:updated', this.handleUpdate);
  }

  disconnectedCallback() {
    window.removeEventListener('wishlist:updated', this.handleUpdate);
  }

  init() {
    if (!this.button) return;

    this.checkIfSaved();

    this.button.addEventListener("click", e => {
      e.preventDefault();
      if (this.button.classList.contains('saved')) {
        this.removeFromWishlist();
      } else {
        this.addToWishlist();
      }
    });
  }

  async getSwymProducts(forceFresh = false) {
    if (!swymProductsPromise || forceFresh) {
      swymProductsPromise = new Promise((resolve) => {
        window._swat.fetchWrtEventTypeET((products) => {
          resolve(products);
        }, 4);
      });
    }
    return swymProductsPromise;
  }

  async checkIfSaved() {
    const products = await this.getSwymProducts();
    const productId = this.dataset.productId;
    const variantId = this.dataset.variantId;

    const isSaved = products.some(p => 
      p.empi.toString() === productId.toString() && 
      p.epi.toString() === variantId.toString()
    );

    isSaved ? this.updateToSavedState() : this.updateToUnsavedState();
    this.button.style.display = 'flex';
  }

  async notifyAllButtons() {
    await this.getSwymProducts(true); 
    
    window.dispatchEvent(new CustomEvent('wishlist:updated'));
  }

  addToWishlist() {
    const productData = this.getProductData();
    this.button.classList.add('is-adding');

    window._swat.addToWishList(productData, () => {
      this.button.classList.remove('is-adding');
      this.notifyAllButtons();
    }, (error) => {
      console.error('Error Swym:', error);
      this.button.classList.remove('is-adding');
    });
  }

  removeFromWishlist() {
    const productData = this.getProductData();
    this.button.classList.add('is-adding');

    window._swat.removeFromWishList(productData, () => {
      this.button.classList.remove('is-adding');
      this.notifyAllButtons();
    }, (error) => {
      console.error('Error Swym:', error);
      this.button.classList.remove('is-adding');
    });
  }

  getProductData() {
    return {
      empi: parseInt(this.dataset.productId),
      epi: parseInt(this.dataset.variantId),
      du: this.dataset.productUrl.split('?')[0],
      dt: this.dataset.productTitle,
      et: 4
    };
  }

  updateToSavedState() {
    const textSpan = this.button.querySelector('.text');
    if (textSpan) textSpan.textContent = 'SAVED';
    this.button.classList.add('saved');
  }

  updateToUnsavedState() {
    const textSpan = this.button.querySelector('.text');
    if (textSpan) textSpan.textContent = 'SAVE';
    this.button.classList.remove('saved');
  }
}

if (!customElements.get('custom-wishlist-button')) {
  customElements.define('custom-wishlist-button', WishlistButton);
}